import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
} from '@mui/material';
import { getDashboardSummary } from '../../services/dashboardService';
import { DashboardSummaryResponse } from '../../types/dashboard';
import useAuth from '../../hooks/useAuth';
import swal from 'sweetalert';
import { useTheme } from '@mui/material/styles';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';

const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardSummaryResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { user } = useAuth();
  const theme = useTheme();

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const data = await getDashboardSummary();
      setDashboardData(data);
    } catch (error) {
      console.error('Erro ao buscar dados da dashboard:', error);
      swal('Erro', 'Não foi possível carregar os dados da dashboard.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading || !dashboardData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Limita às 50 cidades com mais entidades
  const topCities = dashboardData.public_entity_stats.entities_per_city
    .slice()
    .sort((a, b) => b.entity_count - a.entity_count) // Ordena em ordem decrescente
    .slice(0, 50); // Limita para as 50 primeiras

  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      <Grid container spacing={3}>
        {/* Seção de Estatísticas de Usuários */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              bgcolor: theme.palette.primary.light,
              color: theme.palette.primary.contrastText,
              borderRadius: 2,
              textAlign: 'center',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: theme.shadows[6],
              },
            }}
          >
            <Typography variant="h5" gutterBottom>
              Total de Usuários
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
              {dashboardData.user_stats.total_users}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              bgcolor: theme.palette.success.main,
              color: theme.palette.success.contrastText,
              borderRadius: 2,
              textAlign: 'center',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: theme.shadows[6],
              },
            }}
          >
            <Typography variant="h5" gutterBottom>
              Usuários Ativos
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
              {dashboardData.user_stats.active_users}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              bgcolor: theme.palette.warning.light,
              color: theme.palette.warning.contrastText,
              borderRadius: 2,
              textAlign: 'center',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: theme.shadows[6],
              },
            }}
          >
            <Typography variant="h5" gutterBottom>
              Usuários Inativos
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
              {dashboardData.user_stats.inactive_users}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              bgcolor: theme.palette.info.light,
              color: theme.palette.info.contrastText,
              borderRadius: 2,
              textAlign: 'center',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: theme.shadows[6],
              },
            }}
          >
            <Typography variant="h5" gutterBottom>
              Entidades Ativas
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
              {dashboardData.public_entity_stats.active_entities_based_on_users}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Gráfico de Entidades por Cidade */}
      <Box sx={{ mt: 5 }}>
        <Typography variant="h5" gutterBottom>
          Entidades por Cidade (Top 50)
        </Typography>
        <Paper elevation={6} sx={{ height: 500, borderRadius: 2, p: 2 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={topCities}
              margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="city_name" angle={-45} textAnchor="end" interval={0} height={100} />
              <YAxis allowDecimals={false} />
              <RechartsTooltip 
                formatter={(value: number, name: string) => [value, "Número de Entidades"]}
              />
              <Legend verticalAlign="top" height={36} />
              <Bar 
                dataKey="entity_count" 
                name="Número de Entidades" 
                fill={theme.palette.primary.main} 
                animationDuration={1000} 
                animationEasing="ease-out"
              />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;